import { Modal } from '@mantine/core';
import { IconArrowLeft, IconChevronRight } from '@tabler/icons-react';
import classnames from 'classnames';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ALL_EXPENSE_TYPES,
  EXPENSE_TYPE_TREE,
  MainCategory,
  SubCategory,
} from 'src/Constants/expenseCategorization';
import {
  EXPENSE_MAIN_CATEGORIES,
  INCOME_MAIN_CATEGORIES,
  MAIN_CATEGORY_ICONS,
} from 'src/Constants/expenseCategoryIcons';
import useIsMobile from 'src/Hooks/Shared/useIsMobile';
import { CreateExpenseDtoTypeEnum } from 'src/Services/API/generated';

import styles from './CategoryPicker.module.scss';
import { Props } from './CategoryPicker.types';

type Step = 'family' | 'sub' | 'type';

const CategoryPicker = ({ opened, onClose, isIncome, value, onChange }: Props) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  const [step, setStep] = useState<Step>('family');
  const [selectedMain, setSelectedMain] = useState<MainCategory | null>(null);
  const [selectedSub, setSelectedSub] = useState<SubCategory | null>(null);

  const families = isIncome ? INCOME_MAIN_CATEGORIES : EXPENSE_MAIN_CATEGORIES;

  const subCategoriesByMain = useMemo(() => {
    const map = new Map<MainCategory, SubCategory[]>();
    for (const type of ALL_EXPENSE_TYPES) {
      const meta = EXPENSE_TYPE_TREE[type];
      if (meta.isIncome !== isIncome) continue;
      const subs = map.get(meta.main) ?? [];
      if (!subs.includes(meta.sub)) subs.push(meta.sub);
      map.set(meta.main, subs);
    }
    return map;
  }, [isIncome]);

  const typesBySub = useMemo(() => {
    const map = new Map<SubCategory, CreateExpenseDtoTypeEnum[]>();
    for (const type of ALL_EXPENSE_TYPES) {
      const meta = EXPENSE_TYPE_TREE[type];
      if (meta.isIncome !== isIncome) continue;
      const list = map.get(meta.sub) ?? [];
      list.push(type);
      map.set(meta.sub, list);
    }
    return map;
  }, [isIncome]);

  useEffect(() => {
    if (!opened) return;
    if (value && EXPENSE_TYPE_TREE[value] && EXPENSE_TYPE_TREE[value].isIncome === isIncome) {
      setSelectedMain(EXPENSE_TYPE_TREE[value].main);
      setSelectedSub(EXPENSE_TYPE_TREE[value].sub);
    } else {
      setSelectedMain(null);
      setSelectedSub(null);
    }
    setStep('family');
  }, [opened, value, isIncome]);

  const handleSelectFamily = (main: MainCategory) => {
    setSelectedMain(main);
    setSelectedSub(null);
    setStep('sub');
  };

  const handleSelectSub = (sub: SubCategory) => {
    setSelectedSub(sub);
    setStep('type');
  };

  const handleSelectType = (type: CreateExpenseDtoTypeEnum) => {
    onChange(type);
    onClose();
  };

  const goBack = () => {
    if (step === 'type') setStep('sub');
    else if (step === 'sub') setStep('family');
  };

  const title = (() => {
    if (step === 'family')
      return t('expense.form.categoryPicker.chooseFamily', 'Choisir la famille');
    if (step === 'sub' && selectedMain) return t(`expense.mainCategory.${selectedMain}`);
    if (step === 'type' && selectedMain && selectedSub) {
      return `${t(`expense.mainCategory.${selectedMain}`)} › ${t(`expense.subCategory.${selectedSub}`)}`;
    }
    return '';
  })();

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      fullScreen={isMobile}
      size="lg"
      title={
        <div className={styles.header}>
          {step !== 'family' && (
            <button
              type="button"
              className={styles.backButton}
              onClick={goBack}
              aria-label={t('common.back', 'Retour')}
            >
              <IconArrowLeft size={20} />
            </button>
          )}
          <span className={styles.stepTitle}>{title}</span>
        </div>
      }
    >
      <div className={styles.container}>
        {step === 'family' && (
          <div className={styles.familyGrid}>
            {families.map(main => {
              const Icon = MAIN_CATEGORY_ICONS[main];
              const selected = selectedMain === main;
              return (
                <button
                  type="button"
                  key={main}
                  className={classnames(styles.familyCard, {
                    [styles.familyCardSelected]: selected,
                  })}
                  onClick={() => handleSelectFamily(main)}
                >
                  <Icon size={32} className={styles.familyCardIcon} />
                  <span className={styles.familyCardLabel}>
                    {t(`expense.mainCategory.${main}`)}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {step === 'sub' && selectedMain && (
          <div className={styles.list}>
            {(subCategoriesByMain.get(selectedMain) ?? []).map(sub => {
              const selected = selectedSub === sub;
              return (
                <button
                  type="button"
                  key={sub}
                  className={classnames(styles.listItem, {
                    [styles.listItemSelected]: selected,
                  })}
                  onClick={() => handleSelectSub(sub)}
                >
                  <span className={styles.listItemLabel}>{t(`expense.subCategory.${sub}`)}</span>
                  <IconChevronRight size={18} />
                </button>
              );
            })}
          </div>
        )}

        {step === 'type' && selectedSub && (
          <div className={styles.list}>
            {(typesBySub.get(selectedSub) ?? []).map(type => {
              const selected = value === type;
              return (
                <button
                  type="button"
                  key={type}
                  className={classnames(styles.listItem, {
                    [styles.listItemSelected]: selected,
                  })}
                  onClick={() => handleSelectType(type)}
                >
                  <span className={styles.listItemLabel}>{t(`expense.type.${type}`)}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default CategoryPicker;
